<script>
import SelectBatch from "./SelectBatch.vue";
import { money } from "../../../core/forms/money";

export default {
    name: "BatchSelection",
    mixins: [money],
    components: { SelectBatch },
    props: {
        value: Object,
        accountType: {
            type: String,
            required: true
        },
        billCurrency: {
            type: String,
            required: true
        },
        isBundle: {
            type: String,
            required: true,
            validator: (value) => ["bundle", "account"].includes(value),
        }
    },
    data() {
        return {
            selectedBatch: null,
            selectedAmount: null,
            selectedBundle: {},
            currentStep: 1,
            expandedStep: null,
            batches: [],
            availableBundles: [],
            flags: {
                ZWG: '/img/flags/zwl.png',
                USD: '/img/flags/usd.jpg',
            },
        };
    },
    watch: {
        billCurrency(newCurrency) {
            if (this.isBundle === "bundle") {
                this.loadBundles(this.accountType, newCurrency);
            }
        },
        accountType(newType) {
            if (this.isBundle === "bundle") {
                this.loadBundles(newType, this.billCurrency);
            }
        },
        selectedBundle: {
            handler(newBundle) {
                if (newBundle && newBundle.amount) {
                    this.$set(this.selectedBundle, 'amount', newBundle.amount);
                }
            },
            deep: true
        }
    },
    mounted() {
        if (this.isBundle === "bundle") {
            this.fetchBundles();
        }
    },
    methods: {
        fetchBundles() {
            if (this.isBundle !== "bundle") return;

            this.loading = true;

            window.axios
                .get(`/business/lbundles`)
                .then((response) => {
                    let data = response.data.body;

                    if (this.accountType === "ECONET" && this.billCurrency === "ZWG") {
                        this.availableBundles = data.bundles || [];
                    } else if (this.accountType === "ECONET" && this.billCurrency === "USD") {
                        this.availableBundles = data.smart || [];
                    } else if (this.accountType === "NETONE" && this.billCurrency === "USD") {
                        this.availableBundles = data.netone || [];
                    } else {
                        this.availableBundles = [];
                    }
                })
                .catch((error) => {
                    this.$toast.error("Failed to load bundles. Please try again later.", { timeout: 3000 });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadBundles(accountType, billCurrency) {
            if (this.isBundle !== "bundle") return;
            this.fetchBundles();
        },
        toggleStep(step) {
            if (step > this.currentStep) return;
            this.expandedStep = this.expandedStep === step ? null : step;
        },
        nextStep(step) {
            if (step === 2 && !this.selectedBatch) return;

            if (step === 3 && this.isBundle === "bundle" && (!this.selectedBundle || !this.selectedBundle.price)) {
                return;
            }

            if (step === 3 && this.isBundle === "account" && !this.selectedAmount) {
                return;
            }

            this.currentStep = step;
            this.expandedStep = step;
        },
        prevStep(step) {
            this.currentStep = step;
            this.expandedStep = step;
        },
        submitBatch() {
            if (!this.selectedBatch || Object.keys(this.selectedBatch).length === 0) {
                this.$toast.error("Please select a valid batch before submitting.", { timeout: 3000 });
                return;
            }

            if (this.selectedBatch.accounts_count === 0) {
                this.$toast.error("The selected batch has no accounts.", { timeout: 3000 });
                return;
            }

            if (this.isBundle === "account" && (!this.selectedAmount || this.selectedAmount <= 0)) {
                this.$toast.error("Please enter an amount before submitting.", { timeout: 3000 });
                return;
            }

            if (this.isBundle === "bundle" && !this.selectedBundle) {
                this.$toast.error("Please select a bundle before submitting.", { timeout: 3000 });
                return;
            }
            this.$emit("input", [
                {
                    batch_id: this.selectedBatch.id,
                    amount: this.isBundle === "bundle" ? this.selectedBundle.price : this.selectedAmount,
                    bundle_id: this.isBundle === "bundle" ? this.selectedBundle.id : null
                }
            ]);

            this.$toast.success("Batch applied successfully!", { timeout: 3000 });
        },

    }
};
</script>

<template>
    <div class="timeline">
        <!-- Step 1: Select Batch -->
        <div class="timeline-step">
            <div class="step-header" @click="toggleStep(1)">
                <div class="d-flex flex-row align-items-center">
                    <div class="step-number" :class="{ active: currentStep >= 1 }">1</div>
                    <h5 class="text-3 mb-0">Select Batch</h5>
                </div>
                <i class="fas fa-chevron-down" :class="{ rotated: expandedStep === 1 }"></i>
            </div>
            <div v-show="expandedStep === 1" class="step-content">
                <SelectBatch v-model="selectedBatch" :accountType="accountType" />
                <div v-if="selectedBatch?.accounts_count === 0" class="text-center bg-danger rounded-bottom text-white text-center text-1 mt-n3 mx-3">
                    <small >
                        <i class="fa fa-exclamation-triangle mr-2"></i> Selected Batch has no Stored Accounts. Please select another Batch
                    </small>
                </div>
                <button
                    type="button"
                    class="btn btn-primary btn-sm mt-3 ml-3"
                    @click="nextStep(2)"
                    :class="{'bg-dark-5 disabled': !selectedBatch || (selectedBatch?.accounts_count === 0) }"
                >
                    Next
                </button>
            </div>
        </div>

        <!-- Step 2: Set Amount or Select Bundle -->
        <div class="timeline-step">
            <div class="step-header" @click="toggleStep(2)">
                <div class="d-flex flex-row align-items-center">
                    <div class="step-number" :class="{ active: currentStep >= 2 }">2</div>
                    <h5 class="text-3 mb-0">{{ isBundle === "bundle" ? "Select Bundle" : "Set Recharge Amount" }}</h5>
                </div>
                <i class="fas fa-chevron-down" :class="{ rotated: expandedStep === 2 }"></i>
            </div>
            <div v-show="expandedStep === 2" class="step-content">
                <select v-if="isBundle === 'bundle'" v-model="selectedBundle" class="form-control">
                    <option v-for="bundle in availableBundles" v-if="bundle.state === 'ACTIVE'" :key="bundle.id" :value="bundle">
                        {{ billCurrency }} {{ money(bundle.price) }} - {{ bundle.name }} - {{ bundle.category }}
                    </option>
                    <option v-if="availableBundles.length === 0">
                        No active bundles found.
                    </option>
                </select>

                <div v-else class="form-group">
                    <label>Amount</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text input-icon">
                                <img :src="flags[billCurrency]" class="currency-icon mr-1" alt="">
                                {{ billCurrency }}
                            </span>
                        </div>
                        <input
                            type="number"
                            step="0.01"
                            min="0.1"
                            v-model="selectedAmount"
                            class="form-control"
                            placeholder="Amount"
                        >
                    </div>
                </div>

                <button type="button" class="btn btn-primary btn-sm mt-3"
                        @click="nextStep(3)"
                        :class="{'bg-dark-5 disabled': isBundle === 'bundle' ? (!selectedBundle || !selectedBundle.price) : !selectedAmount }"
                >
                    Next
                </button>
            </div>
        </div>
        <!-- Step 3: Confirmation -->
        <div class="timeline-step">
            <div class="step-header" @click="toggleStep(3)">
                <div class="d-flex flex-row align-items-center">
                    <div class="step-number" :class="{ active: currentStep >= 3 }">3</div>
                    <h5 class="text-3 mb-0">Confirm & Apply</h5>
                </div>
                <i class="fas fa-chevron-down" :class="{ rotated: expandedStep === 3 }"></i>
            </div>
            <div v-show="expandedStep === 3" class="step-content">
                <p class="text-muted">Please review your details before submission.</p>

                <ul class="list-group">
                    <li class="list-group-item">
                        <small>Batch Name:</small> <strong>{{ selectedBatch ? selectedBatch.batch_name : "Not Selected" }}</strong>
                    </li>
                    <li class="list-group-item">
                        <small>{{ isBundle ==='bundle' ? "Selected Bundle" : "Amount per Account" }}:</small>
                        <strong>{{ isBundle ==='bundle' ? (selectedBundle ? billCurrency + " "+ money(selectedBundle.price) + " - " + selectedBundle.name + " - " + selectedBundle.category: "Not Selected") : billCurrency + " " + money(selectedAmount) }}</strong>
                    </li>
                    <li class="list-group-item">
                        <small>Accounts in Batch :</small> <strong>{{ selectedBatch?.accounts_count }}</strong>
                    </li>
                    <li class="list-group-item">
                        <small>Total Amount :</small> <strong>{{billCurrency}} {{money((selectedAmount || selectedBundle.price) * selectedBatch?.accounts_count) }}</strong>
                    </li>
                </ul>

                <button type="button" class="btn btn-secondary btn-sm mt-3" @click="prevStep(2)">Back</button>
                <button type="button" class="btn btn-success btn-sm mt-3"
                        @click="submitBatch"
                        :disabled="isBundle === 'bundle' ? !selectedBundle : selectedAmount === null"
                >
                    Apply
                </button>
            </div>
        </div>

    </div>
</template>


<style scoped>
.timeline-step {
    position: relative;
    margin-bottom: 20px;
}

.step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 5px;
    transition: background 0.3s ease-in-out;
}

.step-header:hover {
    background: #e2e6ea;
}

.step-number {
    width: 30px;
    height: 30px;
    background-color: #ccc;
    border-radius: 50%;
    text-align: center;
    line-height: 30px;
    color: white;
    margin-right: 10px;
    transition: background 0.3s ease-in-out;
}

.step-number.active {
    background-color: #252c65;
}

.step-content {
    background: white;
    padding: 15px;
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    width: 100%;
    margin-top: 10px;
}

.fas {
    transition: transform 0.3s ease-in-out;
}

.rotated {
    transform: rotate(180deg);
}
</style>
